import React, { ChangeEvent, Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';

import {
	Box,
	Button,
	FormControl,
	IconButton,
	InputLabel,
	makeStyles,
	MenuItem,
	Select,
	SvgIconTypeMap,
	TextField,
	Typography
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClearIcon from '@material-ui/icons/Clear';
import RefreshIcon from '@material-ui/icons/Refresh';

import SimpleTextInfo from '../common/SimpleTextInfo';
import JsonDisplay from '../common/JsonDisplay/JsonDisplay';
import JsonDisplayModal from '../common/JsonDisplay/JsonDisplayModal';
import { getRequest } from '../../utils/httpClient';

const useStyles = makeStyles(() => ({
	modalDataCardHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: '1rem',
		paddingBottom: '1vh',
		marginBottom: '1vh'
	},
	searchControl: {
		flex: 1,
		maxWidth: '400px'
	},
	centralBox: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		maxHeight: '80vh',
		minHeight: '80vh'
	}
}));

export const AlignmentViewer: FC<AlignmentViewerProps> = (props) => {
	const { id, sport, open, setOpen, icon } = props;
	const classes = useStyles();
	const [pbpIdFilter, setPbpIdFilter] = useState<string | null>(null);
	const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

	const {
		isLoading,
		data: alignmentData,
		isError,
		refetch,
		remove
	} = useQuery(
		['alignment', id, selectedVersionId],
		() =>
			getRequest(
				'sports-data',
				`/getLatestAlignment?airingId=${id}&sport=${sport}${selectedVersionId ? `&versionId=${encodeURIComponent(selectedVersionId)}` : ''}`
			),
		{
			enabled: open
		}
	);

	useEffect(() => {
		if (!open) {
			remove();
			setPbpIdFilter(null);
			setSelectedVersionId(null);
		}
	}, [open, remove]);

	const isVersionedResponse = (payload: unknown): payload is VersionedAlignmentResponse => {
		return Boolean(payload && typeof payload === 'object' && 'file' in payload && 'versions' in payload);
	};

	const rawAlignmentPayload = alignmentData;
	const versionedAlignment = isVersionedResponse(rawAlignmentPayload) ? rawAlignmentPayload : null;
	const alignmentFile = versionedAlignment?.file ?? rawAlignmentPayload;

	const sortedVersions = useMemo(() => {
		if (!versionedAlignment?.versions?.length) return [];

		return [...versionedAlignment.versions].sort((a, b) => {
			const aDate = dayjs(a.LastModified).valueOf();
			const bDate = dayjs(b.LastModified).valueOf();
			return bDate - aDate;
		});
	}, [versionedAlignment]);

	const latestVersionId = sortedVersions.find((version) => version.IsLatest)?.VersionId ?? sortedVersions[0]?.VersionId ?? '';
	const versionSelectValue = selectedVersionId ?? latestVersionId;

	let filteredAlignment = null;
	if (alignmentFile?.data) {
		try {
			filteredAlignment = JSON.parse(alignmentFile.data);
		} catch {
			filteredAlignment = null;
		}
	}

	if (pbpIdFilter && filteredAlignment) {
		filteredAlignment = {
			...filteredAlignment,
			playByPlay: filteredAlignment.playByPlay.filter((alignment: { id: string }) => {
				return alignment.id === pbpIdFilter;
			})
		};
	}

	const debouncedClick = useCallback(
		debounce(
			() => {
				refetch();
			},
			2000,
			{ leading: true, trailing: false, maxWait: 2000 }
		),
		[refetch]
	);

	const onVersionChange = (event: ChangeEvent<{ value: unknown }>): void => {
		const nextVersionId = event.target.value as string;
		setSelectedVersionId(nextVersionId || null);
	};

	return (
		<JsonDisplayModal title={'Alignment Viewer'} setOpen={setOpen} open={open} icon={icon}>
			{isLoading && (
				<Box className={classes.centralBox}>
					<CircularProgress />
				</Box>
			)}

			{isError && (
				<Box className={classes.centralBox}>
					<Typography variant='subtitle2'>Error getting alignment</Typography>{' '}
				</Box>
			)}

			{alignmentData && (
				<>
					<Box className={classes.modalDataCardHeader}>
						{sortedVersions.length > 0 && (
							<FormControl variant='outlined' size='small' className={classes.searchControl}>
								<InputLabel id='alignment-version-select-label'>Alignment Version</InputLabel>
								<Select
									labelId='alignment-version-select-label'
									id='alignment-version-select'
									value={versionSelectValue}
									onChange={onVersionChange}
									label='Alignment Version'>
									{sortedVersions.map((version) => (
										<MenuItem key={version.VersionId} value={version.VersionId}>
											{version.IsLatest && 'Latest - '}
											{dayjs(version.LastModified).format('DD/MM/YYYY')}
											<strong style={{ marginLeft: '4px' }}>{dayjs(version.LastModified).format('HH:mm:ss')}</strong>
										</MenuItem>
									))}
								</Select>
							</FormControl>
						)}
						<TextField
							className={classes.searchControl}
							size='small'
							id='id'
							label='Search PlayByPlay ID'
							name='id'
							value={pbpIdFilter ?? ''}
							onChange={(e): void => setPbpIdFilter(e.target.value)}
							variant='outlined'
							InputProps={{
								endAdornment: pbpIdFilter ? (
									<IconButton size='small' onClick={(): void => setPbpIdFilter(null)}>
										<ClearIcon />
									</IconButton>
								) : undefined
							}}
						/>
						<Button color='primary' onClick={(): void => debouncedClick()}>
							<RefreshIcon />
						</Button>
					</Box>
					<>
						<SimpleTextInfo label='File Name' value={alignmentFile?.key || 'not specified'} />
						{!versionedAlignment && (
							<SimpleTextInfo label='Last Modified' value={dayjs(alignmentFile?.lastModified ?? '').format('DD/MM/YYYY HH:mm') || 'not specified'} />
						)}
						<SimpleTextInfo label='Data' value={''} />
						<JsonDisplay data={filteredAlignment} />
					</>
				</>
			)}
		</JsonDisplayModal>
	);
};

type AlignmentViewerData = {
	data?: string;
	key?: string;
	lastModified?: string;
};

type AlignmentVersion = {
	VersionId: string;
	LastModified: string;
	IsLatest?: boolean;
};

type VersionedAlignmentResponse = {
	file: AlignmentViewerData;
	versions: AlignmentVersion[];
};

type AlignmentViewerProps = { id: string; sport: string; open: boolean; setOpen: Dispatch<SetStateAction<boolean>>; icon: SvgIconTypeMap };

export default AlignmentViewer;
